import tippy from 'tippy.js';
import {
    applyInspirationShoppingListHtml,
    getActiveInspirationListing,
    refreshInspirationShoppingList,
    syncInspirationAddToCartButton,
} from '../modules/inspirations/inspiration-api';
import { Sidebar } from '../modules/Sidebar';

class Minicart {
    constructor() {
        this.sidebar = null;
        this.contentHost = null;
        this.mode = 'header';
        this.highlightCartItemKey = '';
        this.sourceProductId = 0;
        this.quantityTimer = null;
        this.isLoading = false;
        this.cartDirty = true;
        this.pendingQuantityKey = '';
        this.addedCartItemKeys = [];
        this.initialBootstrapDone = false;

        if (typeof ajax === 'undefined') {
            return;
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
            return;
        }

        this.init();
    }

    init() {
        this.sidebar = new Sidebar({
            overlayClassNames: ['bg-grey-900/60'],
        });

        this.contentHost = document.createElement('div');
        this.sidebar.content.appendChild(this.contentHost);

        document.addEventListener('click', this.onDocumentClick.bind(this), true);
        document.addEventListener('change', this.onDocumentChange.bind(this), true);
        this.sidebar.content.addEventListener('click', this.onSidebarClick.bind(this));
        this.sidebar.content.addEventListener('change', this.onSidebarChange.bind(this));

        this.bootstrapInitialSidebar();

        window.mdaMinicart = this;
    }

    bootstrapInitialSidebar() {
        if (this.initialBootstrapDone) {
            return;
        }

        const template = document.getElementById('mda-minicart-initial-sidebar');

        if (!template) {
            return;
        }

        const html = template.innerHTML.trim();

        if (html === '') {
            return;
        }

        this.applySidebarHtml(html, {
            mode: 'header',
            sourceProductId: this.getPageProductId(),
        });
        this.initialBootstrapDone = true;
    }

    getPageProductId() {
        const productRoot = document.querySelector('[id^="product-"]');

        if (!productRoot) {
            return 0;
        }

        const productId = parseInt(productRoot.id.replace('product-', ''), 10);

        return Number.isNaN(productId) ? 0 : productId;
    }

    resolveFormProductId(form, formData) {
        const addToCartField = form.querySelector('[name="add-to-cart"]');
        const addToCartValue =
            addToCartField && 'value' in addToCartField
                ? String(addToCartField.value || '')
                : '';

        const productId = parseInt(
            form.dataset.product_id ||
                formData.get('product_id') ||
                addToCartValue ||
                formData.get('add-to-cart') ||
                String(this.getPageProductId() || 0),
            10
        );

        return Number.isNaN(productId) ? 0 : productId;
    }

    isInspirationSidebarContext(node) {
        return Boolean(node?.closest?.('[data-inspiration-sidebar]'));
    }

    isInspirationSidebarOpen() {
        return Boolean(getActiveInspirationListing());
    }

    findCartItemElement(cartItemKey, scope = document) {
        if (!cartItemKey) {
            return null;
        }

        const root = scope && typeof scope.querySelector === 'function' ? scope : document;

        return root.querySelector(
            `[data-minicart-cart-item][data-cart-item-key="${cartItemKey}"]`
        ) || root.querySelector(
            `[data-minicart-recommendation] [data-minicart-remove-item][data-cart-item-key="${cartItemKey}"]`
        )?.closest('[data-minicart-recommendation]');
    }

    getInspirationSidebarContent() {
        const listing = getActiveInspirationListing();

        return listing?.inspirationSidebar?.content || null;
    }

    shouldRefreshInspirationSidebar(cartItemKey = '', sourceNode = null) {
        if (sourceNode && this.isInspirationSidebarContext(sourceNode)) {
            return true;
        }

        if (!this.isInspirationSidebarOpen()) {
            return false;
        }

        const inspirationContent = this.getInspirationSidebarContent();

        if (!inspirationContent) {
            return false;
        }

        if (!cartItemKey) {
            return true;
        }

        return Boolean(
            inspirationContent.querySelector(
                `[data-minicart-cart-item][data-cart-item-key="${cartItemKey}"]`
            )
            || inspirationContent.querySelector(
                `[data-minicart-remove-item][data-cart-item-key="${cartItemKey}"]`
            )
        );
    }

    async refreshInspirationShoppingListIfOpen() {
        await refreshInspirationShoppingList();
    }

    closeInspirationSidebarIfOpen() {
        const listing = getActiveInspirationListing();
        const inspirationSidebar = listing?.inspirationSidebar;

        if (
            !inspirationSidebar ||
            (!inspirationSidebar.isOpen &&
                !inspirationSidebar.root?.classList.contains('-active'))
        ) {
            return;
        }

        inspirationSidebar.close();
    }

    resolveCartItemElement(cartItemKey, sourceNode = null) {
        if (sourceNode && this.isInspirationSidebarContext(sourceNode)) {
            const inspirationContent = this.getInspirationSidebarContent();

            return (
                sourceNode.closest('[data-minicart-recommendation]')
                || sourceNode.closest('[data-minicart-cart-item]')
                || (inspirationContent
                    ? this.findCartItemElement(cartItemKey, inspirationContent)
                    : null)
            );
        }

        return this.findCartItemElement(cartItemKey);
    }

    initTooltips(root = this.contentHost) {
        if (!root) {
            return;
        }

        root.querySelectorAll('[js-tippy]').forEach(element => {
            if (element._tippy) {
                return;
            }

            tippy(element, {
                content: element.getAttribute('js-tippy'),
                allowHTML: true,
            });
        });
    }

    initSidebarEnhancements(root = this.contentHost) {
        this.initTooltips(root);

        if (typeof window.mdaInitQuantityInputs === 'function') {
            window.mdaInitQuantityInputs(root);
        }

        const inspirationSidebar =
            root?.matches?.('[data-inspiration-sidebar]')
                ? root
                : root?.closest?.('[data-inspiration-sidebar]');

        if (inspirationSidebar && !inspirationSidebar._mdaMinicartEventsBound) {
            inspirationSidebar._mdaMinicartEventsBound = true;
            inspirationSidebar.addEventListener(
                'click',
                this.onInspirationSidebarClick.bind(this)
            );
            inspirationSidebar.addEventListener(
                'change',
                this.onInspirationSidebarChange.bind(this)
            );
        }
    }

    onInspirationSidebarClick(event) {
        const removeButton = event.target.closest('[data-minicart-remove-item]');

        if (removeButton) {
            event.preventDefault();
            event.stopPropagation();

            const cartItemKey = removeButton.getAttribute('data-cart-item-key');

            if (cartItemKey) {
                this.removeCartItem(cartItemKey, removeButton).catch(console.error);
            }

            return;
        }

        const addRecommendationButton = event.target.closest(
            '[data-minicart-add-recommendation]'
        );

        if (addRecommendationButton) {
            event.preventDefault();
            event.stopPropagation();
            this.handleAddRecommendationClick(addRecommendationButton);
        }
    }

    onInspirationSidebarChange(event) {
        const input = event.target.closest('[data-minicart-quantity] input');

        if (!input) {
            return;
        }

        const cartItem = input.closest('[data-minicart-cart-item]');

        if (cartItem) {
            this.scheduleCartItemQuantityUpdate(cartItem, input);
            return;
        }

        const recommendedRow = input.closest('[data-minicart-recommendation]');

        if (recommendedRow) {
            this.scheduleCartItemQuantityUpdate(recommendedRow, input);
            return;
        }

        const recommendedActions = input.closest('[data-product-recommended-actions]');
        const cartItemKey = recommendedActions
            ?.querySelector('[data-minicart-remove-item]')
            ?.getAttribute('data-cart-item-key');

        if (!cartItemKey) {
            return;
        }

        clearTimeout(this.quantityTimer);

        this.quantityTimer = setTimeout(() => {
            const quantity = Math.max(0, parseInt(input.value || '0', 10));
            this.updateCartItem(cartItemKey, quantity, input).catch(console.error);
        }, 600);
    }

    isProductRecommendedItem(node) {
        return Boolean(node?.closest?.('[data-product-recommended-item]'));
    }

    getProductRecommendedItem(node) {
        return node?.closest?.('[data-product-recommended-item]') || null;
    }

    isMinicartSidebarRecommendation(node) {
        return Boolean(
            node?.closest?.('[data-minicart-sidebar-content]')
            && node?.closest?.('[data-minicart-recommendation]')
        );
    }

    syncAddedCartItemKeys(keys) {
        if (!Array.isArray(keys)) {
            return;
        }

        this.addedCartItemKeys = keys.filter(Boolean);
    }

    buildSidebarRequestPayload(extra = {}) {
        return {
            mode: this.mode,
            highlight_cart_item_key: this.highlightCartItemKey,
            added_cart_item_keys: this.addedCartItemKeys.join(','),
            source_product_id: this.sourceProductId || this.getPageProductId(),
            ...extra,
        };
    }

    updateProductRecommendedActions(article, data) {
        if (!article || !data?.recommendation_actions_html) {
            return;
        }

        const currentActions = article.querySelector(
            '[data-product-recommended-actions]'
        );

        if (!currentActions) {
            return;
        }

        currentActions.outerHTML = data.recommendation_actions_html;

        if (typeof window.mdaInitQuantityInputs === 'function') {
            window.mdaInitQuantityInputs(article);
        }
    }

    updateRecommendationRowActions(trigger, data) {
        if (!trigger || !data?.recommendation_actions_html) {
            return false;
        }

        const row =
            trigger.closest('[data-product-recommended-item]')
            || trigger.closest('[data-minicart-recommendation]');

        const currentActions =
            row?.querySelector('[data-product-recommended-actions]')
            || trigger.closest('[data-product-recommended-actions]');

        if (!currentActions) {
            return false;
        }

        currentActions.outerHTML = data.recommendation_actions_html;

        if (row && typeof window.mdaInitQuantityInputs === 'function') {
            window.mdaInitQuantityInputs(row);
        }

        if (this.isInspirationSidebarContext(trigger)) {
            syncInspirationAddToCartButton(getActiveInspirationListing());
        }

        return true;
    }

    findProductPageRecommendationArticle(productId, variationId = 0) {
        const normalizedProductId = String(productId || '0');
        const normalizedVariationId = String(variationId || '0');

        return (
            Array.from(document.querySelectorAll('[data-product-recommended-item]')).find(
                (article) =>
                    article.dataset.productId === normalizedProductId
                    && article.dataset.variationId === normalizedVariationId
            ) || null
        );
    }

    syncProductPageRecommendationActions(data) {
        if (!data?.recommendation_actions_html) {
            return;
        }

        const productId = data.recommendation_product_id ?? data.product_id;
        const variationId = data.recommendation_variation_id ?? data.variation_id ?? 0;
        const article = this.findProductPageRecommendationArticle(productId, variationId);

        if (article) {
            this.updateProductRecommendedActions(article, data);
        }
    }

    buildProductRecommendationPayload(article) {
        if (!article) {
            return {};
        }

        return {
            recommendation_context: 'product',
            product_id: article.dataset.productId || '0',
            variation_id: article.dataset.variationId || '0',
        };
    }

    async updateProductRecommendedQuantity(cartItemKey, quantity, article) {
        article?.classList.add('opacity-60', 'pointer-events-none');
        this.markCartDirty();

        try {
            const data = await this.request('update', {
                cart_item_key: cartItemKey,
                quantity,
                mode: this.mode,
                highlight_cart_item_key: this.highlightCartItemKey,
                ...this.buildProductRecommendationPayload(article),
            });

            this.updateProductRecommendedActions(article, data);
        } finally {
            article?.classList.remove('opacity-60', 'pointer-events-none');
        }
    }

    async removeProductRecommendedItem(cartItemKey, article) {
        article?.classList.add('opacity-60', 'pointer-events-none');
        this.markCartDirty();

        try {
            const data = await this.request('remove', {
                cart_item_key: cartItemKey,
                mode: this.mode,
                highlight_cart_item_key: this.highlightCartItemKey,
                ...this.buildProductRecommendationPayload(article),
            });

            this.updateProductRecommendedActions(article, data);
        } finally {
            article?.classList.remove('opacity-60', 'pointer-events-none');
        }
    }

    scheduleProductRecommendedQuantityUpdate(article, input) {
        const cartItemKey = article
            ?.querySelector('[data-minicart-remove-item]')
            ?.getAttribute('data-cart-item-key');

        if (!article || !cartItemKey) {
            return;
        }

        clearTimeout(this.quantityTimer);

        this.quantityTimer = setTimeout(() => {
            const quantity = Math.max(0, parseInt(input.value || '0', 10));
            this.updateProductRecommendedQuantity(cartItemKey, quantity, article).catch(
                console.error
            );
        }, 600);
    }

    showSidebarLoader() {
        if (!this.contentHost) {
            return;
        }

        this.contentHost.innerHTML =
            '<div class="minicart-sidebar__loader flex items-center justify-center px-24 py-48 text-1614! text-text-secondary">Ładowanie...</div>';
    }

    applySidebarHtml(html, state = {}) {
        if (!this.contentHost) {
            return;
        }

        this.mode = state.mode || this.mode;
        this.highlightCartItemKey = state.highlightCartItemKey || '';
        this.sourceProductId = state.sourceProductId || this.sourceProductId;
        this.contentHost.innerHTML = html || '';
        this.cartDirty = false;
        this.initSidebarEnhancements();
    }

    markCartDirty() {
        this.cartDirty = true;
    }

    isSidebarOpen() {
        return Boolean(this.sidebar?.root?.classList.contains('-active'));
    }

    notifyCartChanged() {
        this.markCartDirty();
        void this.refreshSidebarIfOpen();
    }

    async refreshSidebarIfOpen() {
        if (!this.isSidebarOpen()) {
            return;
        }

        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.showSidebarLoader();

        try {
            await this.renderSidebar({
                mode: this.mode,
                cart_item_key: this.highlightCartItemKey,
                source_product_id:
                    this.sourceProductId || this.getPageProductId(),
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    canReuseSidebarCache() {
        return (
            !this.cartDirty &&
            this.mode === 'header' &&
            Boolean(this.contentHost?.innerHTML.trim())
        );
    }

    async request(type, payload = {}) {
        const formData = new FormData();
        formData.append('action', 'mda_minicart_action');
        formData.append('type', type);
        formData.append('nonce', ajax.nonce);

        Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, String(value));
            }
        });

        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
        });

        const result = await response.json();

        if (!result?.success) {
            throw new Error(result?.data?.message || 'Minicart request failed');
        }

        return result.data;
    }

    async renderSidebar(payload = {}) {
        const data = await this.request('sidebar', this.buildSidebarRequestPayload({
            mode: payload.mode || this.mode,
            cart_item_key: payload.cart_item_key || this.highlightCartItemKey,
            source_product_id:
                payload.source_product_id ||
                this.sourceProductId ||
                this.getPageProductId(),
        }));

        this.syncAddedCartItemKeys(data.added_cart_item_keys);

        this.applySidebarHtml(data.html || '', {
            mode: payload.mode || this.mode,
            highlightCartItemKey: payload.cart_item_key || this.highlightCartItemKey,
            sourceProductId:
                payload.source_product_id ||
                this.sourceProductId ||
                this.getPageProductId(),
        });
    }

    async loadHtml(html, state = {}) {
        this.applySidebarHtml(html, state);
    }

    async openFromHeader() {
        if (this.isLoading) {
            return;
        }

        if (this.canReuseSidebarCache()) {
            this.sidebar.open();
            return;
        }

        this.isLoading = true;
        this.sidebar.open();
        this.showSidebarLoader();

        try {
            this.mode = 'header';
            this.highlightCartItemKey = '';
            this.addedCartItemKeys = [];
            this.sourceProductId = this.getPageProductId();

            await this.renderSidebar({
                mode: 'header',
                source_product_id: this.sourceProductId,
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    async openAfterAdd(html, state = {}) {
        this.closeInspirationSidebarIfOpen();

        await this.loadHtml(html, {
            mode: 'after_add',
            ...state,
        });

        if (!this.sidebar) {
            return;
        }

        this.sidebar.bringToFront();
        this.sidebar.open();
    }

    async addFromProductForm(form) {
        if (this.isLoading || !(form instanceof HTMLFormElement)) {
            return;
        }

        const formData = new FormData(form);
        const isVariationForm = form.classList.contains('variations_form');
        const parentProductId = this.resolveFormProductId(form, formData);
        let variationId = parseInt(formData.get('variation_id') || '0', 10);
        const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10));

        if (isVariationForm && !variationId && typeof window.mdaAutoSelectSingleVariation === 'function') {
            window.mdaAutoSelectSingleVariation(form);
            variationId = parseInt(form.querySelector('[name="variation_id"]')?.value || '0', 10);
        }

        const payload = {
            product_id: parentProductId,
            variation_id: isVariationForm ? variationId : 0,
            quantity,
            source_product_id: parentProductId || this.getPageProductId(),
        };

        for (const [key, value] of formData.entries()) {
            if (key.startsWith('attribute_')) {
                payload[key] = value;
            }
        }

        this.isLoading = true;
        this.markCartDirty();

        try {
            const data = await this.request('add', payload);

            this.mode = 'after_add';
            this.highlightCartItemKey = data.cart_item_key || '';
            this.syncAddedCartItemKeys(
                data.added_cart_item_keys
                || (data.cart_item_key ? [data.cart_item_key] : [])
            );
            this.sourceProductId = payload.source_product_id;

            await this.openAfterAdd(data.html, {
                highlightCartItemKey: this.highlightCartItemKey,
                sourceProductId: this.sourceProductId,
            });
        } catch (error) {
            console.error(error);
            window.alert(error?.message || 'Nie udało się dodać produktu do koszyka.');
        } finally {
            this.isLoading = false;
        }
    }

    getCartItemQuantity(cartItemKey) {
        const item = this.findCartItemElement(cartItemKey);

        if (!item) {
            return 1;
        }

        const input = item.querySelector('[data-minicart-quantity] input');

        return Math.max(1, parseInt(input?.value || '1', 10));
    }

    getRecommendationQuantity(productId) {
        const item = document.querySelector(
            `[data-product-id="${productId}"][data-minicart-recommendation]`
        );

        if (!item) {
            return 1;
        }

        const input = item.querySelector('[data-minicart-quantity] input');

        return Math.max(1, parseInt(input?.value || '1', 10));
    }

    async updateCartItem(cartItemKey, quantity, sourceNode = null) {
        if (this.pendingQuantityKey === cartItemKey) {
            return;
        }

        const refreshInspiration = this.shouldRefreshInspirationSidebar(
            cartItemKey,
            sourceNode
        );
        const cartItem = this.resolveCartItemElement(cartItemKey, sourceNode);

        this.pendingQuantityKey = cartItemKey;
        cartItem?.classList.add('opacity-60', 'pointer-events-none');
        this.markCartDirty();

        try {
            const data = await this.request('update', {
                cart_item_key: cartItemKey,
                quantity,
                ...this.buildSidebarRequestPayload(),
            });

            this.syncAddedCartItemKeys(data.added_cart_item_keys);

            if (refreshInspiration) {
                await this.refreshInspirationShoppingListIfOpen();

                if (this.isSidebarOpen()) {
                    this.markCartDirty();
                    await this.refreshSidebarIfOpen();
                }
            } else {
                await this.loadHtml(data.html, {
                    mode: this.mode,
                    highlightCartItemKey: this.highlightCartItemKey,
                    sourceProductId: this.sourceProductId,
                });
                this.syncProductPageRecommendationActions(data);
            }
        } finally {
            this.pendingQuantityKey = '';
            cartItem?.classList.remove('opacity-60', 'pointer-events-none');
        }
    }

    async removeCartItem(cartItemKey, sourceNode = null) {
        const fromInspiration =
            sourceNode && this.isInspirationSidebarContext(sourceNode);
        const refreshInspiration =
            fromInspiration
            || this.shouldRefreshInspirationSidebar(cartItemKey, sourceNode);
        const cartItem = this.resolveCartItemElement(cartItemKey, sourceNode);
        const payload = {
            cart_item_key: cartItemKey,
            ...this.buildSidebarRequestPayload(),
        };

        if (fromInspiration) {
            const termId = Number(
                sourceNode
                    .closest('[data-inspiration-sidebar]')
                    ?.querySelector('[data-inspiration-add-all-to-cart]')
                    ?.getAttribute('data-term-id') || 0
            );

            if (termId > 0) {
                payload.inspiration_term_id = termId;
            }
        }

        this.markCartDirty();
        cartItem?.classList.add('opacity-60', 'pointer-events-none');

        try {
            const data = await this.request('remove', payload);

            this.syncAddedCartItemKeys(data.added_cart_item_keys);

            if (this.highlightCartItemKey === cartItemKey) {
                this.highlightCartItemKey = this.addedCartItemKeys[0] || '';
            }

            if (this.mode === 'after_add' && this.addedCartItemKeys.length === 0) {
                this.mode = 'header';
                this.highlightCartItemKey = '';
            }

            if (fromInspiration && typeof data.inspiration_shopping_list_html === 'string') {
                applyInspirationShoppingListHtml(
                    getActiveInspirationListing(),
                    data.inspiration_shopping_list_html,
                    Boolean(data.all_products_in_cart)
                );
                this.notifyCartChanged();
                return;
            }

            if (refreshInspiration) {
                await this.refreshInspirationShoppingListIfOpen();
                this.notifyCartChanged();
                return;
            }

            await this.loadHtml(data.html, {
                mode: this.mode,
                highlightCartItemKey: this.highlightCartItemKey,
                sourceProductId: this.sourceProductId,
            });
            this.syncProductPageRecommendationActions(data);

            if (!this.contentHost.querySelector('[data-minicart-cart-item]')) {
                this.mode = 'header';
                this.highlightCartItemKey = '';
            }
        } finally {
            cartItem?.classList.remove('opacity-60', 'pointer-events-none');
        }
    }

    async addRecommendation(productId, variationId = 0, options = {}) {
        if (this.isLoading || productId <= 0) {
            return;
        }

        const inInspiration = options.inInspiration === true;
        const inProductRecommendation = options.inProductRecommendation === true;
        const inMinicartSidebarRecommendation =
            options.inMinicartSidebarRecommendation === true;
        const article = options.article || null;
        const trigger = options.trigger || null;

        this.isLoading = true;
        this.markCartDirty();

        try {
            const requestPayload = {
                product_id: productId,
                variation_id: variationId,
                quantity: 1,
                source_product_id: this.sourceProductId || this.getPageProductId(),
            };

            if (inProductRecommendation) {
                Object.assign(requestPayload, this.buildProductRecommendationPayload(article));
            } else if (inMinicartSidebarRecommendation) {
                requestPayload.minicart_context = 'after_add_recommendation';
                requestPayload.added_cart_item_keys = this.addedCartItemKeys.join(',');
            } else if (inInspiration && trigger) {
                const termId = Number(
                    trigger
                        .closest('[data-inspiration-sidebar]')
                        ?.querySelector('[data-inspiration-add-all-to-cart]')
                        ?.getAttribute('data-term-id') || 0
                );

                if (termId > 0) {
                    requestPayload.inspiration_term_id = termId;
                }
            }

            const data = await this.request('add', requestPayload);

            if (inInspiration) {
                if (typeof data.inspiration_shopping_list_html === 'string') {
                    applyInspirationShoppingListHtml(
                        getActiveInspirationListing(),
                        data.inspiration_shopping_list_html,
                        Boolean(data.all_products_in_cart)
                    );
                } else {
                    this.updateRecommendationRowActions(trigger, data);
                    syncInspirationAddToCartButton(
                        getActiveInspirationListing(),
                        Boolean(data.all_products_in_cart)
                    );
                }

                this.mode = 'after_add';
                this.highlightCartItemKey = data.cart_item_key || '';
                this.syncAddedCartItemKeys(
                    data.added_cart_item_keys
                    || (data.cart_item_key ? [data.cart_item_key] : [])
                );
                this.sourceProductId =
                    this.sourceProductId || this.getPageProductId();

                await this.openAfterAdd(data.html, {
                    highlightCartItemKey: this.highlightCartItemKey,
                    sourceProductId: this.sourceProductId,
                });

                return;
            }

            if (inProductRecommendation && article) {
                this.updateProductRecommendedActions(article, data);
                this.mode = 'after_add';
                this.highlightCartItemKey = data.cart_item_key || '';
                this.syncAddedCartItemKeys(
                    data.added_cart_item_keys
                    || (data.cart_item_key ? [data.cart_item_key] : [])
                );
                this.sourceProductId = this.sourceProductId || this.getPageProductId();

                await this.openAfterAdd(data.html, {
                    highlightCartItemKey: this.highlightCartItemKey,
                    sourceProductId: this.sourceProductId,
                });

                return;
            }

            if (inMinicartSidebarRecommendation) {
                this.updateRecommendationRowActions(trigger, data);
                this.mode = 'after_add';
                this.highlightCartItemKey = data.cart_item_key || this.highlightCartItemKey;
                this.syncAddedCartItemKeys(
                    data.added_cart_item_keys
                    || [...this.addedCartItemKeys, data.cart_item_key].filter(Boolean)
                );

                await this.loadHtml(data.html, {
                    mode: 'after_add',
                    highlightCartItemKey: this.highlightCartItemKey,
                    sourceProductId: this.sourceProductId || this.getPageProductId(),
                });
                this.syncProductPageRecommendationActions(data);

                return;
            }

            this.mode = 'after_add';
            this.highlightCartItemKey = data.cart_item_key || '';
            this.syncAddedCartItemKeys(
                data.added_cart_item_keys
                || (data.cart_item_key ? [data.cart_item_key] : [])
            );

            await this.openAfterAdd(data.html, {
                highlightCartItemKey: this.highlightCartItemKey,
                sourceProductId: this.sourceProductId || this.getPageProductId(),
            });
        } catch (error) {
            console.error(error);
            window.alert(error?.message || 'Nie udało się dodać produktu do koszyka.');
        } finally {
            this.isLoading = false;
        }
    }

    handleAddRecommendationClick(button) {
        const productId = parseInt(button.getAttribute('data-product-id') || '0', 10);
        const variationId = parseInt(button.getAttribute('data-variation-id') || '0', 10);
        const article = this.getProductRecommendedItem(button);
        const inMinicartSidebarRecommendation =
            this.isMinicartSidebarRecommendation(button);

        if (productId > 0) {
            this.addRecommendation(productId, variationId, {
                inInspiration: this.isInspirationSidebarContext(button),
                inProductRecommendation: Boolean(article) && !inMinicartSidebarRecommendation,
                inMinicartSidebarRecommendation,
                article,
                trigger: button,
            });
        }
    }

    onDocumentClick(event) {
        const addRecommendationButton = event.target.closest(
            '[data-minicart-add-recommendation]'
        );

        if (addRecommendationButton) {
            event.preventDefault();
            event.stopPropagation();
            this.handleAddRecommendationClick(addRecommendationButton);
            return;
        }

        const removeButton = event.target.closest('[data-minicart-remove-item]');

        if (removeButton && this.isInspirationSidebarContext(removeButton)) {
            event.preventDefault();
            event.stopPropagation();

            const cartItemKey = removeButton.getAttribute('data-cart-item-key');

            if (cartItemKey) {
                this.removeCartItem(cartItemKey, removeButton).catch(console.error);
            }

            return;
        }

        if (removeButton) {
            const recommendedItem = this.getProductRecommendedItem(removeButton);
            const sidebarCartItem = removeButton.closest('[data-minicart-cart-item]');

            if (
                recommendedItem
                && (!sidebarCartItem || !this.contentHost?.contains(sidebarCartItem))
            ) {
                event.preventDefault();
                event.stopPropagation();

                const cartItemKey = removeButton.getAttribute('data-cart-item-key');

                if (cartItemKey) {
                    this.removeProductRecommendedItem(cartItemKey, recommendedItem).catch(
                        console.error
                    );
                }

                return;
            }
        }

        const cartTrigger = event.target.closest('[js-trigger-menu-element="cart"]');

        if (!cartTrigger) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.openFromHeader();
    }

    onSidebarClick(event) {
        const continueButton = event.target.closest('.minicart-sidebar__continue-btn');
        if (continueButton) {
            event.preventDefault();
            this.sidebar.close();
            return;
        }

        const removeButton = event.target.closest('[data-minicart-remove-item]');
        if (removeButton) {
            event.preventDefault();
            const cartItemKey = removeButton.getAttribute('data-cart-item-key');

            if (cartItemKey) {
                this.removeCartItem(cartItemKey, removeButton).catch(console.error);
            }
            return;
        }

    }

    onSidebarChange(event) {
        const input = event.target.closest('[data-minicart-quantity] input');

        if (!input) {
            return;
        }

        const cartItem = input.closest('[data-minicart-cart-item]');

        if (cartItem && this.contentHost?.contains(cartItem)) {
            this.scheduleCartItemQuantityUpdate(cartItem, input);
            return;
        }

        const recommendedItem = input.closest('[data-minicart-recommendation]');

        if (!recommendedItem || !this.contentHost?.contains(recommendedItem)) {
            return;
        }

        this.scheduleCartItemQuantityUpdate(recommendedItem, input);
    }

    onDocumentChange(event) {
        const input = event.target.closest('[data-minicart-quantity] input');

        if (!input) {
            return;
        }

        const cartItem = input.closest('[data-minicart-cart-item]');

        if (cartItem && this.contentHost?.contains(cartItem)) {
            return;
        }

        const recommendedItem = this.getProductRecommendedItem(input);

        if (recommendedItem) {
            this.scheduleProductRecommendedQuantityUpdate(recommendedItem, input);
            return;
        }

        if (cartItem && this.isInspirationSidebarContext(cartItem)) {
            this.scheduleCartItemQuantityUpdate(cartItem, input);
        }
    }

    scheduleCartItemQuantityUpdate(cartItem, input) {
        const cartItemKey =
            cartItem.getAttribute('data-cart-item-key')
            || cartItem
                .querySelector('[data-minicart-remove-item]')
                ?.getAttribute('data-cart-item-key');

        if (!cartItemKey) {
            return;
        }

        clearTimeout(this.quantityTimer);

        this.quantityTimer = setTimeout(() => {
            const quantity = Math.max(0, parseInt(input.value || '0', 10));
            this.updateCartItem(cartItemKey, quantity, input).catch(console.error);
        }, 600);
    }
}

new Minicart();
